import { useCallback, useEffect, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import { shareText } from '../lib/characterQuiz.js';

const WIDTH = 1080;
const HEIGHT = 1920;
const SHARE_URL = 'https://scripturejourney.net/quiz';

/**
 * The shareable result card.
 *
 * Drawn on a canvas rather than styled in the page, because the thing people
 * actually want is a file: something that can be dropped into an Instagram
 * story or a TikTok without cropping the browser chrome out of a screenshot.
 * 1080×1920 is the story frame, and everything important sits inside the middle
 * two thirds so a platform's own furniture does not cover it.
 *
 * ── Fonts ───────────────────────────────────────────────────────────────────
 *
 * The brief asked for EB Garamond. The app loads no external fonts — there is
 * no font CDN in the CSP and nothing here should depend on the network at the
 * moment somebody hits Share — so this uses the same serif stack as the rest of
 * the app, which resolves to a real Palatino or Georgia on nearly every device.
 * Canvas silently substitutes a font it does not have, so naming a webfont here
 * would produce a card that looked fine in testing and shipped as Arial.
 *
 * ── Getting it to them ──────────────────────────────────────────────────────
 *
 * Web Share with a file is the good path: it opens the native sheet with
 * Instagram and TikTok in it. Not every browser has it, and support for sharing
 * *files* is narrower than for sharing links, so the fallback is a download and
 * the long-press instruction that people already know. Both are offered rather
 * than detected-and-hidden, because a share sheet that silently does nothing is
 * worse than a button that says Save.
 */
export default function ShareCard({ character, onClose }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [status, setStatus] = useState(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const serif = "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
    const sans = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

    const INK = '#f2f0e9';
    const GOLD = '#c9a84c';
    const GOLD_SOFT = '#e8d59a';
    const MUTED = '#a9b3c6';

    // Ground: deep navy with a warm bloom behind the name, so the card has a
    // light source rather than being a flat rectangle.
    ctx.fillStyle = '#080e1a';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    const bloom = ctx.createRadialGradient(WIDTH / 2, 620, 40, WIDTH / 2, 620, 760);
    bloom.addColorStop(0, 'rgba(201, 168, 76, 0.20)');
    bloom.addColorStop(1, 'rgba(201, 168, 76, 0)');
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // A hairline rule inset from the edge — the manuscript border the app uses.
    ctx.strokeStyle = 'rgba(201, 168, 76, 0.28)';
    ctx.lineWidth = 2;
    ctx.strokeRect(54, 54, WIDTH - 108, HEIGHT - 108);

    ctx.textAlign = 'center';

    // Cross ornament
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 7;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(WIDTH / 2, 250);
    ctx.lineTo(WIDTH / 2, 372);
    ctx.moveTo(WIDTH / 2 - 44, 296);
    ctx.lineTo(WIDTH / 2 + 44, 296);
    ctx.stroke();

    ctx.fillStyle = MUTED;
    ctx.font = `500 34px ${sans}`;
    ctx.letterSpacing = '7px';
    ctx.fillText('YOU ARE MOST LIKE', WIDTH / 2, 470);
    ctx.letterSpacing = '0px';

    ctx.font = `140px ${serif}`;
    ctx.fillText(character.emoji, WIDTH / 2, 660);

    // The name, sized down if it will not fit — "The Prodigal Son" is nearly
    // three times the width of "Ruth".
    let nameSize = 128;
    ctx.font = `700 ${nameSize}px ${serif}`;
    while (ctx.measureText(character.name).width > WIDTH - 220 && nameSize > 64) {
      nameSize -= 4;
      ctx.font = `700 ${nameSize}px ${serif}`;
    }
    ctx.fillStyle = GOLD_SOFT;
    ctx.fillText(character.name, WIDTH / 2, 820);

    ctx.fillStyle = MUTED;
    ctx.font = `italic 40px ${serif}`;
    ctx.fillText(character.tagline, WIDTH / 2, 890);

    ctx.strokeStyle = 'rgba(201, 168, 76, 0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(WIDTH / 2 - 120, 960);
    ctx.lineTo(WIDTH / 2 + 120, 960);
    ctx.stroke();

    // The verse, wrapped by measuring rather than guessing at a character count.
    ctx.fillStyle = INK;
    ctx.font = `italic 48px ${serif}`;
    const words = `“${character.verse.text}”`.split(' ');
    const lines = [];
    let line = '';
    for (const word of words) {
      const attempt = line ? `${line} ${word}` : word;
      if (ctx.measureText(attempt).width > WIDTH - 260 && line) {
        lines.push(line);
        line = word;
      } else {
        line = attempt;
      }
    }
    if (line) lines.push(line);
    let y = 1150;
    for (const l of lines) {
      ctx.fillText(l, WIDTH / 2, y);
      y += 74;
    }

    ctx.fillStyle = GOLD;
    ctx.font = `500 38px ${sans}`;
    ctx.fillText(character.verse.ref, WIDTH / 2, y + 42);

    // A closing rule, mirroring the one above the verse, so the lower half
    // has a floor rather than trailing off into empty navy.
    ctx.strokeStyle = 'rgba(201, 168, 76, 0.4)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(WIDTH / 2 - 120, y + 130);
    ctx.lineTo(WIDTH / 2 + 120, y + 130);
    ctx.stroke();

    // Branding, at the foot, above the safe margin for story UI.
    ctx.fillStyle = GOLD_SOFT;
    ctx.font = `600 46px ${serif}`;
    ctx.fillText('Scripture Journey', WIDTH / 2, HEIGHT - 330);
    ctx.fillStyle = MUTED;
    ctx.font = `400 32px ${sans}`;
    ctx.letterSpacing = '3px';
    ctx.fillText('scripturejourney.net/quiz', WIDTH / 2, HEIGHT - 272);
    ctx.letterSpacing = '0px';

    setDataUrl(canvas.toDataURL('image/png'));
  }, [character]);

  useEffect(() => {
    draw();
  }, [draw]);

  const toFile = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return new File([blob], `scripture-journey-${character.id}.png`, { type: 'image/png' });
  }, [character]);

  const share = useCallback(async () => {
    const file = await toFile();
    const text = shareText(character, SHARE_URL);
    try {
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text });
        return;
      }
      if (navigator.share) {
        await navigator.share({ text, url: SHARE_URL });
        return;
      }
      await navigator.clipboard.writeText(text);
      setStatus('Copied. Paste it anywhere.');
    } catch (error) {
      // A cancelled share sheet throws AbortError. Saying "that failed" when
      // somebody simply changed their mind would be a lie.
      if (error?.name !== 'AbortError') setStatus('Couldn’t open sharing — save the image instead.');
    }
  }, [character, toFile]);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Share your result">
      <button className="modal__scrim" onClick={onClose} aria-label="Close" tabIndex={-1} />
      <div className="modal__sheet share-sheet">
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={14} />
        </button>

        <h2 className="modal__title">Share this</h2>
        <p className="modal__sub">Built for a story — 1080 by 1920.</p>

        <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} className="share-sheet__canvas" />
        {dataUrl ? (
          <img className="share-sheet__preview" src={dataUrl} alt={`${character.name} result card`} />
        ) : null}

        <button type="button" className="btn btn--primary btn--block btn--lg" onClick={share}>
          Share
        </button>

        {dataUrl ? (
          <a
            className="btn btn--ghost btn--block"
            href={dataUrl}
            download={`scripture-journey-${character.id}.png`}
          >
            Save the image
          </a>
        ) : null}

        <p className="share-sheet__hint">
          {status ?? 'On a phone you can also press and hold the picture to save it.'}
        </p>
      </div>
    </div>
  );
}
